import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Sparkles, Send } from "lucide-react";

export default function SignaturesBlock() {
  const [count, setCount] = useState(null);
  const [recent, setRecent] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", email: "", support_text: "", public: true });
  const [status, setStatus] = useState({ type: "", msg: "" });

  const load = async () => {
    try {
      const [c, r] = await Promise.all([
        api.get("/signatures/count"),
        api.get("/signatures?limit=12"),
      ]);
      setCount(c.data.total);
      setRecent(r.data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ type: "loading", msg: "" });
    try {
      await api.post("/signatures", form);
      setStatus({ type: "ok", msg: "Merci. Ta signature a été enregistrée." });
      setForm({ name: "", location: "", email: "", support_text: "", public: true });
      load();
    } catch (err) {
      setStatus({ type: "err", msg: formatApiError(err.response?.data?.detail) || "Erreur" });
    }
  };

  return (
    <section className="border-b-2 border-[#1A1A18] bg-[#FAF8F3]" data-testid="signatures-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-10 md:gap-16">
        <div className="lg:col-span-5">
          <div className="font-mono-label text-[#4A7A3D] mb-4 flex items-center gap-2"><Sparkles size={14} /> Mobilisation</div>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.02]">Soutenir le plaidoyer.</h2>
          <div className="mt-10 border-2 border-[#1A1A18] p-6 bg-[#1A1A18] text-[#FAF8F3]">
            <div className="font-mono-label text-[#C4944A] mb-2">SIGNATAIRES</div>
            <div className="font-display text-7xl md:text-8xl text-[#C4944A] leading-none" data-testid="signatures-count">
              {count === null ? "—" : count.toLocaleString("fr-FR")}
            </div>
            <p className="text-[#FAF8F3]/70 mt-4 max-w-sm">
              Chaque signature compte. Plus nous sommes nombreux, plus nous pesons dans le débat public mauricien.
            </p>
          </div>

          {recent.length > 0 && (
            <div className="mt-8">
              <div className="font-mono-label text-[#4B4B48] mb-3">Dernières signatures</div>
              <div className="flex flex-wrap gap-2" data-testid="signatures-wall">
                {recent.map((s, i) => (
                  <span key={i} className="font-mono-label bg-[#F5EFEB] border border-[#E0D8CE] px-3 py-1.5 text-[#1A1A18]">
                    {s.name}{s.location ? ` · ${s.location}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="border-2 border-[#1A1A18] p-6 md:p-8 bg-[#F5EFEB] space-y-5" data-testid="signatures-form">
            <div>
              <div className="font-mono-label text-[#4A7A3D] mb-2">Je signe le plaidoyer</div>
              <p className="text-sm text-[#4B4B48]">
                Je soutiens la légalisation encadrée du cannabis à l'île Maurice, fondée sur la science, le droit et la dignité humaine.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="font-mono-label text-[#4B4B48] block mb-1">Nom (ou pseudo)</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 outline-none focus:border-[#4A7A3D]" data-testid="signatures-name" />
              </div>
              <div>
                <label className="font-mono-label text-[#4B4B48] block mb-1">Ville / quartier</label>
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 outline-none focus:border-[#4A7A3D]" data-testid="signatures-location" />
              </div>
            </div>
            <div>
              <label className="font-mono-label text-[#4B4B48] block mb-1">Email (privé, non publié)</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-transparent border-b-2 border-[#1A1A18] w-full py-2 outline-none focus:border-[#4A7A3D]" data-testid="signatures-email" />
            </div>
            <div>
              <label className="font-mono-label text-[#4B4B48] block mb-1">Mot de soutien (optionnel)</label>
              <textarea rows={2} maxLength={240} value={form.support_text} onChange={(e) => setForm({ ...form, support_text: e.target.value })}
                className="bg-transparent border-2 border-[#E0D8CE] w-full p-2 outline-none focus:border-[#4A7A3D]" data-testid="signatures-support" />
            </div>
            <label className="flex items-center gap-2 font-mono-label text-[#1A1A18] cursor-pointer">
              <input type="checkbox" checked={form.public} onChange={(e) => setForm({ ...form, public: e.target.checked })} className="w-5 h-5" data-testid="signatures-public" />
              Afficher mon nom sur le mur des signataires
            </label>
            {status.msg && (
              <div className={`font-mono-label ${status.type === "err" ? "text-red-700" : "text-[#4A7A3D]"}`} data-testid="signatures-status">{status.msg}</div>
            )}
            <button type="submit" className="bg-[#4A7A3D] text-[#FAF8F3] px-6 py-3 font-mono-label flex items-center gap-2 hover:bg-[#3E6633]" data-testid="signatures-submit">
              <Send size={14} /> Je signe
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
